import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLinkIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { resolveBacklogRefusal } from '@/pages/Meetings/components/Copilot/assistBacklogReason'
import { ApiError } from '@/services/api'
import { fetchMeetingById } from '@/services/meetings'
import type { BriefBacklogTicket, BriefData } from '@/services/meetingBrief'
import BriefAgendaSection from './BriefAgendaSection'
import {
  avatarTone,
  formatAssignedTasks,
  formatBacklogBreakdown,
  formatCommentMeta,
  formatDueBadge,
  formatTicketCount,
  formatWeekdayDate,
} from './briefFormat'

const STATUS_STYLES: Record<string, string> = {
  'In Progress':
    'border-blue-100 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300',
  Open: 'border-[var(--border-color)] bg-[var(--bg-tertiary)] text-[var(--text-secondary)]',
  Resolved:
    'border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
  Closed:
    'border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
}

const DEFAULT_STATUS_STYLE =
  'border-[var(--border-color)] bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'

function TicketRow({ ticket, now }: { ticket: BriefBacklogTicket; now: number }) {
  const { t, locale } = useUiStrings()
  const due = formatDueBadge(ticket, now, locale, t)

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5 text-xs transition-colors hover:border-[var(--border-hover)] sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <a
          href={ticket.url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded font-semibold text-[var(--accent)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          {ticket.key}
        </a>
        <span
          className="min-w-0 flex-1 truncate text-[11px] text-[var(--text-secondary)]"
          title={ticket.title}
        >
          {ticket.title}
        </span>
        <span className="flex shrink-0 items-center gap-1 pl-0 sm:pl-1">
          <span
            className={`flex h-4 w-4 items-center justify-center rounded-full border text-[9px] font-bold ${avatarTone(ticket.assignee.name)}`}
            aria-hidden="true"
          >
            {ticket.assignee.initials}
          </span>
          <span className="text-[11px] text-[var(--text-secondary)]" title={ticket.assignee.name}>
            {ticket.assignee.name.trim().split(/\s+/).slice(-1)[0]}
          </span>
        </span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:pl-2">
        <span
          className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[ticket.status] ?? DEFAULT_STATUS_STYLE}`}
        >
          {ticket.status}
        </span>
        {due && (
          <span
            className={`rounded-md border px-2 py-0.5 text-[10px] ${
              due.overdue
                ? 'border-rose-100 bg-rose-50 text-rose-600 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300'
                : 'border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)]'
            }`}
          >
            {due.label}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * The three answers the modal exists for: what was decided last time, what the
 * tickets are doing now, and what changed in the comments since.
 */
export default function BriefContent({ data, now }: { data: BriefData; now: number }) {
  const { t, locale } = useUiStrings()
  const navigate = useNavigate()
  const previous = data.previousMeeting
  const openPreviousMinutes = useCallback(async () => {
    const meetingId = previous.openMeetingId ?? previous.referencedMeetingId
    if (!meetingId) {
      navigate('/history')
      return
    }
    try {
      await fetchMeetingById(meetingId)
      navigate(`/meetings/${meetingId}`)
    } catch (error) {
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
        const owner = previous.owner
        const ownerName = owner?.name?.trim() ?? ''
        const ownerEmail = owner?.email?.trim() ?? ''
        window.alert(
          ownerName && ownerEmail
            ? fmt(t.briefMinutesAccessDenied, {
                title: previous.title,
                ownerName,
                ownerEmail,
              })
            : fmt(t.briefMinutesAccessDeniedNoOwner, { title: previous.title })
        )
      }
    }
  }, [
    navigate,
    previous.openMeetingId,
    previous.referencedMeetingId,
    previous.owner,
    previous.title,
    t.briefMinutesAccessDenied,
    t.briefMinutesAccessDeniedNoOwner,
  ])
  const ticketsMessage = data.ticketsUnavailableReason
    ? resolveBacklogRefusal(
        { code: 'not_supported', message: '', reason: data.ticketsUnavailableReason },
        t
      ).message
    : null

  return (
    <div className="space-y-5" data-testid="brief-content">
      <BriefAgendaSection agenda={data.agenda} />

      <section className="space-y-2.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[13px] font-bold text-[var(--text-primary)]">
            {t.briefPreviousMeeting}
          </h3>
          <button
            type="button"
            onClick={() => void openPreviousMinutes()}
            className="inline-flex shrink-0 items-center gap-0.5 rounded text-[11px] font-medium text-[var(--accent)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <span>{t.briefOpenMinutes}</span>
            <ExternalLinkIcon className="h-3 w-3" aria-hidden="true" />
          </button>
        </div>

        <p className="text-[11px] leading-normal text-[var(--text-secondary)]">
          {previous.number > 0
            ? fmt(t.briefMeetingMeta, {
                number: previous.number,
                title: previous.title,
                date: formatWeekdayDate(previous.heldAt, locale),
                duration: previous.durationMinutes,
              })
            : fmt(t.briefMeetingMetaPlain, {
                title: previous.title,
                date: formatWeekdayDate(previous.heldAt, locale),
                duration: previous.durationMinutes,
              })}
        </p>

        <p className="text-xs leading-relaxed text-[var(--text-secondary)]">{data.summary}</p>

        <div className="grid gap-3 border-t border-[var(--border-color)] pt-3 sm:grid-cols-2">
          <div>
            <h4 className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
              {t.briefAgreed}
            </h4>
            <ul className="mt-1.5 space-y-1.5">
              {data.agreedPoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-1.5 text-[11px] leading-snug text-[var(--text-secondary)]"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] font-bold text-[var(--text-secondary)]">
              {t.briefAssigned}
            </h4>
            <ul className="mt-1.5 space-y-1.5">
              <li className="flex items-start gap-1.5 text-[11px] leading-snug text-[var(--text-secondary)]">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--text-tertiary)]" />
                <span>{formatAssignedTasks(data.assignedTasks, t)}</span>
              </li>
              {data.assignedTasks.items.map((item) => (
                <li
                  key={`${item.content}:${item.issueKey ?? ''}`}
                  className="flex items-start gap-1.5 text-[11px] leading-snug text-[var(--text-secondary)]"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--text-tertiary)]" />
                  <span>
                    {item.content}
                    {item.assignee ? ` · ${item.assignee}` : ''}
                    {item.issueKey ? ` · ${item.issueKey}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="space-y-2.5">
        <div>
          <h3 className="text-[13px] font-bold leading-none text-[var(--text-primary)]">
            {t.briefBacklogTitle}
          </h3>
          <p className="mt-1 text-[11px] text-[var(--text-secondary)]">
            {ticketsMessage
              ? ticketsMessage
              : data.backlogTickets.length === 0
                ? t.briefNoTickets
                : `${formatTicketCount(data.backlogTickets.length, t)} · ${formatBacklogBreakdown(
                    data.backlogTickets,
                    t
                  )}`}
          </p>
        </div>

        {data.backlogTickets.length > 0 && (
          <div className="space-y-1.5">
            {data.backlogTickets.map((ticket) => (
              <TicketRow key={ticket.key} ticket={ticket} now={now} />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2.5">
        <div>
          <h3 className="text-[13px] font-bold leading-none text-[var(--text-primary)]">
            {t.briefCommentsTitle}
          </h3>
          <p className="mt-1 text-[11px] text-[var(--text-secondary)]">{t.briefCommentsHint}</p>
        </div>

        <div className="space-y-2">
          {data.backlogTickets.length === 0 && (
            <p className="rounded-xl border border-dashed border-[var(--border-color)] px-3 py-3 text-center text-[11px] text-[var(--text-tertiary)]">
              {t.briefNoTickets}
            </p>
          )}

          {data.backlogTickets.map((ticket) => {
            const comment = data.commentSummaries.find(
              (candidate) => candidate.ticketKey === ticket.key
            )

            if (!comment) {
              return (
                <div
                  key={ticket.key}
                  className="flex items-center gap-2 rounded-xl border border-dashed border-[var(--border-color)] px-3 py-2.5 text-[11px] text-[var(--text-tertiary)]"
                >
                  <span className="font-bold text-[var(--accent)]">{ticket.key}</span>
                  <span>{t.briefNoComments}</span>
                </div>
              )
            }

            return (
              <div
                key={ticket.key}
                className="space-y-1.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 shadow-sm"
              >
                <p className="text-xs">
                  <span className="mr-1.5 font-bold text-[var(--accent)]">{comment.ticketKey}</span>
                  <span className="text-[11px] leading-relaxed text-[var(--text-secondary)]">
                    {comment.summary}
                  </span>
                </p>
                <p className="pl-0.5 text-[10px] text-[var(--text-tertiary)]">
                  {formatCommentMeta(comment, now, locale, t)}
                </p>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
